"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/shadcn/utils";
import { Button } from "@/lib/shadcn/ui/button";
import { Calendar } from "@/lib/shadcn/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/shadcn/ui/popover";

export interface DatePickerProps {
    onChange: (date: Date) => void;
    variant?: "default" | "destructive" | "outline" | "secondary" | "volt" | "ghost" | "link";
    selectedValue?: Date;
    /** Accessible name for the trigger button (WCAG 1.3.1 / 4.1.2). */
    ariaLabel?: string;
}

export function DatePicker({ onChange, variant = "default", selectedValue, ariaLabel }: DatePickerProps) {
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState<Date | undefined>(selectedValue);

    // Sync internal state with selectedValue prop changes
    React.useEffect(() => {
        if (selectedValue) {
            setDate(selectedValue);
        }
    }, [selectedValue]);

    return (
        <div className="flex flex-col gap-3">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={variant}
                        aria-label={ariaLabel}
                        className={cn("w-[240px] justify-start text-left font-normal", !date && "text-muted-foreground")}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : <span>Pick a date</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={date}
                        captionLayout="dropdown"
                        onSelect={(newDate) => {
                            if (!newDate) return;
                            setDate(newDate);
                            onChange(newDate);
                            setOpen(false); // Close popover after selection
                        }}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
